import { createContext, useContext, type RefObject } from 'react'
import type { Attachment, Me, Message } from '../../api/types'
import type { LocalChannel } from '../../db/db'
import type { UserMap } from '../../lib/channels'
import type { EditFormHandle } from './EditForm'

/** A message being edited in place. */
export interface MessageEdit {
  messageId: string
  /** Focus goes here when the edit ends: by default the message. */
  returnFocus: HTMLElement | null
  /** The edit form while it shows, to tell whether its text has changed. */
  form: RefObject<EditFormHandle | null>
  /** Counts the times the form was asked to take focus again. */
  focusRequests: number
}

export interface ConversationContextValue {
  channel: LocalChannel
  me: Me
  users: UserMap
  canModerate: boolean
  messagesById: Map<string, Message>
  startReply: (message: Message) => void
  jumpToMessage: (messageId: string, seq?: number) => void
  openAttachment: (attachment: Attachment) => void
  /** Forward a message to other conversations: opens the forward dialog. */
  requestForward: (message: Message) => void
  requestDelete: (message: Message) => void
  /** The message being edited in place: one at a time. */
  editing: MessageEdit | null
  /**
   * Edit a message in place. Asked for the message already being edited, its form takes focus again. While another
   * edit has unsaved changes, that edit stays open and takes focus instead, with a toast to finish or cancel it first.
   */
  startEdit: (message: Message, returnFocus?: HTMLElement | null) => void
  /** Ends the edit of this message, if it is still the one being edited. */
  endEdit: (messageId: string) => void
}

export const ConversationContext = createContext<ConversationContextValue | null>(null)

export function useConversation(): ConversationContextValue {
  const value = useContext(ConversationContext)
  if (!value) throw new Error('useConversation outside ConversationContext')
  return value
}
